import {
  Button,
  Checkbox,
  Form,
  Input,
  InputNumber,
  Space,
  Switch,
  TimePicker,
} from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { dbTimeFormat } from "../../../constants/constants";
import {
  useAddBusinessShiftMutation,
  useGetBusinessShiftDetailsQuery,
  useUpdateBusinessShiftMutation,
} from "../../../features/businessShift/businessShiftApi";
import useError from "../../../hooks/useError";
import { toast } from "../../../lib/toast";
import { ModalComponentProps } from "../../../types";
import { convertToSnakeCase } from "../../../utils/utils";
import CustomModal from "../../ui/CustomModal";
import Loader from "../../ui/Loader";

export default function ManageShift({
  data,
  type,
  handleClose,
  ...rest
}: ModalComponentProps) {
  const [form] = Form.useForm();
  const [isHalfDayEnable, setIsHalfDayEnable] = useState(false);
  const {
    isLoading: isShiftLoading,
    isError: isShiftDetailsError,
    error: shiftDetailsError,
    data: shiftDetails,
  } = useGetBusinessShiftDetailsQuery(data || "", {
    skip: !data && type !== "UPDATE",
  });
  const [addBusinessShift, { isLoading, isError, isSuccess, error }] =
    useAddBusinessShiftMutation();
  const [
    updateBusinessShift,
    {
      isLoading: isUpdateLoading,
      isError: isUpdateError,
      isSuccess: isUpdateSuccess,
      error: updateError,
    },
  ] = useUpdateBusinessShiftMutation();
  // error toast message
  useError(isError, error, "Shift added fail");
  useError(isUpdateError, updateError, "Shift updated fail");
  useError(isShiftDetailsError, shiftDetailsError, "Get shift fail");

  const detailsData = shiftDetails?.data;

  const onFinish = (values: any) => {
    const {
      title,
      start_time,
      end_time,
      checkin_grace_enable,
      checkin_grace_time,
      checkout_grace_enable,
      checkout_grace_time,
      ...rest
    } = values;

    const newValues = {
      ...rest,
      title,
      name: convertToSnakeCase(title),
      start_time: dayjs(start_time)?.format(dbTimeFormat),
      end_time: dayjs(end_time)?.format(dbTimeFormat),
      checkin_grace_time: checkin_grace_time || 0,
      checkout_grace_time: checkout_grace_time || 0,
      checkin_grace_enable: checkin_grace_enable ? 1 : 0,
      checkout_grace_enable: checkout_grace_enable ? 1 : 0,
      is_halfday_enable: isHalfDayEnable ? 1 : 0,
      // color_code:
    };

    if (type === "UPDATE" && data) {
      return updateBusinessShift({
        body: {
          ...newValues,
          old_settings: {
            name: detailsData?.name,
            title: detailsData?.title,
            end_time: detailsData?.end_time,
            start_time: detailsData?.start_time,
            is_halfday_enable: detailsData?.is_halfday_enable,
            checkin_grace_time: detailsData?.checkin_grace_time,
            checkout_grace_time: detailsData?.checkout_grace_time,
            checkin_grace_enable: detailsData?.checkin_grace_enable,
            checkout_grace_enable: detailsData?.checkout_grace_enable,
          },
        },
        id: data,
      });
    }

    return addBusinessShift(newValues);
  };

  useEffect(() => {
    if (Object.keys(detailsData || {}) && type === "UPDATE") {
      form.setFieldsValue({
        title: detailsData?.title,
        start_time: detailsData?.start_time
          ? dayjs(detailsData?.start_time, dbTimeFormat)
          : undefined,
        end_time: detailsData?.end_time
          ? dayjs(detailsData?.end_time, dbTimeFormat)
          : undefined,
        checkin_grace_time: detailsData?.checkin_grace_time,
        checkout_grace_time: detailsData?.checkout_grace_time,
        checkin_grace_enable: detailsData?.checkin_grace_enable,
        checkout_grace_enable: detailsData?.checkout_grace_enable,
        max_office_hours: detailsData?.max_office_hours,
        is_halfday_enable: detailsData?.is_halfday_enable,
      });

      setIsHalfDayEnable(detailsData?.is_halfday_enable ? true : false);
    }
  }, [form, detailsData, type]);

  // success toast message
  useEffect(() => {
    if (isSuccess) {
      toast("success", "Shift added successfully");
      return handleClose();
    }
    if (isUpdateSuccess) {
      toast("success", "Shift updated successfully");
      return handleClose();
    }
  }, [handleClose, isSuccess, isUpdateSuccess]);

  return (
    <CustomModal
      title={
        type === "UPDATE" ? "Update Shift Information" : "Add Shift Information"
      }
      {...rest}
      handleClose={handleClose}
      width={800}
      footer={null}
    >
      <Form
        className="custom-form"
        layout="vertical"
        requiredMark="optional"
        size="large"
        onFinish={onFinish}
        form={form}
      >
        {isShiftLoading ? (
          <Loader />
        ) : (
          <>
            <Form.Item
              name="title"
              label="Shift Name"
              className="mb-4"
              rules={[
                {
                  required: true,
                  message: "Please enter shift name",
                },
              ]}
            >
              <Input size="large" placeholder="Ex: Banani B Building" />
            </Form.Item>
            <div className="grid md:grid-cols-2 gap-6 mb-2">
              <div className="p-4 rounded-md border-2 border-dashed border-display-light">
                <Form.Item
                  name="start_time"
                  label="Start Time"
                  className="mb-4"
                  rules={[
                    {
                      required: true,
                      message: "Please enter start time",
                    },
                  ]}
                >
                  <TimePicker
                    className="w-full"
                    placeholder="HH:MM"
                    format={"hh:mm A"}
                  />
                </Form.Item>
                <Form.Item
                  name="checkin_grace_enable"
                  className="mb-2"
                  valuePropName="checked"
                >
                  <Checkbox className="text-header text-sm font-medium">
                    Allow Grace Period
                  </Checkbox>
                </Form.Item>
                <Form.Item
                  noStyle
                  shouldUpdate={(prevValues, currentValues) =>
                    prevValues.checkin_grace_enable !==
                    currentValues.checkin_grace_enable
                  }
                >
                  {({ getFieldValue }) =>
                    getFieldValue("checkin_grace_enable") ? (
                      <Form.Item
                        name="checkin_grace_time"
                        label="Grace Time (minutes)"
                        className="mb-1"
                        rules={[
                          {
                            validator: async (_: any, value: number) => {
                              if (!value) {
                                return Promise.reject(
                                  new Error("Please enter grace time")
                                );
                              }

                              if (Number(value) < 0) {
                                return Promise.reject(
                                  new Error("Grace time can't be negative")
                                );
                              }

                              if (Number(value) > 1440) {
                                return Promise.reject(
                                  new Error(
                                    "Grace time cannot exceed 1440 minutes"
                                  )
                                );
                              }
                            },
                          },
                        ]}
                      >
                        <InputNumber placeholder="Ex: 15" className="w-full" />
                      </Form.Item>
                    ) : null
                  }
                </Form.Item>
                <p className="text-body-text text-sm font-normal ">
                  Set grace time for office check-in
                </p>
              </div>
              <div className="p-4 rounded-md border-2 border-dashed border-display-light">
                <Form.Item
                  name="end_time"
                  label="End Time"
                  className="mb-4"
                  rules={[
                    {
                      required: true,
                      message: "Please enter end time",
                    },
                    // ({ getFieldValue }) => ({
                    //   validator(_, value) {
                    //     const startTime = getFieldValue("start_time");
                    //     if (!value || !startTime || value.isAfter(startTime)) {
                    //       return Promise.resolve();
                    //     }
                    //     return Promise.reject(
                    //       new Error(
                    //         "End time cannot be earlier than the start time"
                    //       )
                    //     );
                    //   },
                    // }),
                  ]}
                >
                  <TimePicker
                    className="w-full"
                    placeholder="HH:MM"
                    format={"hh:mm A"}
                  />
                </Form.Item>
                <Form.Item
                  name="checkout_grace_enable"
                  className="mb-2"
                  valuePropName="checked"
                >
                  <Checkbox className="text-header text-sm font-medium">
                    Allow Grace Period
                  </Checkbox>
                </Form.Item>
                <Form.Item
                  noStyle
                  shouldUpdate={(prevValues, currentValues) =>
                    prevValues.checkout_grace_enable !==
                    currentValues.checkout_grace_enable
                  }
                >
                  {({ getFieldValue }) =>
                    getFieldValue("checkout_grace_enable") ? (
                      <Form.Item
                        name="checkout_grace_time"
                        label="Grace Time (minutes)"
                        className="mb-1"
                        rules={[
                          {
                            validator: async (_: any, value: number) => {
                              if (!value) {
                                return Promise.reject(
                                  new Error("Please enter grace time")
                                );
                              }

                              if (Number(value) < 0) {
                                return Promise.reject(
                                  new Error("Grace time can't be negative")
                                );
                              }
                              if (Number(value) > 1440) {
                                return Promise.reject(
                                  new Error(
                                    "Grace time cannot exceed 1440 minutes"
                                  )
                                );
                              }
                            },
                          },
                        ]}
                      >
                        <InputNumber placeholder="Ex: 15" className="w-full" />
                      </Form.Item>
                    ) : null
                  }
                </Form.Item>
                <p className="text-body-text text-sm font-normal ">
                  Set grace time for office check-out
                </p>
              </div>
            </div>
            <Form.Item
              name="max_office_hours"
              label="Max Office Hours"
              className="mb-4"
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value) {
                      return Promise.reject(
                        new Error("Please enter max office hours")
                      );
                    }

                    if (Number(value) < 0) {
                      return Promise.reject(
                        new Error("Max office hours can't be negative")
                      );
                    }
                    if (Number(value) > 24) {
                      return Promise.reject(
                        new Error("Max office hours cannot exceed 24 hours")
                      );
                    }

                    if (!Number.isInteger(value)) {
                      return Promise.reject(
                        new Error("Max office hours must be integer")
                      );
                    }

                    const startTime = getFieldValue("start_time");
                    const endTime = getFieldValue("end_time");

                    if (!startTime || !endTime) {
                      return Promise.resolve();
                    }

                    const duration = endTime?.diff(startTime, "hours");
                    if (value < duration) {
                      return Promise.reject(
                        new Error(
                          `Max Office Hours cannot be less than the required duration of (${duration} hours)`
                        )
                      );
                    }

                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <InputNumber
                size="large"
                placeholder="Enter max office hours"
                className="w-full"
                // decimalSeparator="."
                // formatter={(val, info) => {
                //   if (info?.input) {
                //     return info?.input.toString();
                //   }
                //   return val?.toString() || "";
                // }}
                // parser={(value) => {
                //   if (!value) return "";

                //   const cleanedValue = value.replace(/[^\d.-]/g, "");

                //   const [integerPart, decimalPart] = cleanedValue.split(".");

                //   if (decimalPart !== undefined) {
                //     return `${integerPart}.${decimalPart}`;
                //   }

                //   return integerPart;
                // }}
              />
            </Form.Item>

            <div className="flex items-center gap-6 my-4">
              <h6 className="text-header text-base font-medium w-[100px]">
                Half day:
              </h6>
              <div className="flex items-center gap-2">
                <Switch
                  checked={isHalfDayEnable}
                  onChange={(checked) => setIsHalfDayEnable(checked)}
                />
                <span className="text-header text-sm font-medium">
                  {isHalfDayEnable ? "Activated" : "Deactivated"}
                  <span className="text-extra-gray text-sm font-normal">
                    {isHalfDayEnable
                      ? "- Activate to allow employees to take half shift leave"
                      : "- Deactivate to disallow employees from taking half shift leave"}
                  </span>
                </span>
              </div>
            </div>
          </>
        )}

        <Space className="w-full mt-2" size={12}>
          <Button
            className="normal-dark-button modal-button"
            onClick={handleClose}
            size="middle"
            disabled={isLoading || isUpdateLoading || isShiftLoading}
          >
            Cancel
          </Button>
          <Button
            className="modal-button"
            type="primary"
            htmlType="submit"
            size="middle"
            loading={isLoading || isUpdateLoading}
            disabled={isShiftLoading}
          >
            {type === "UPDATE" ? "Update" : "Save"}
          </Button>
        </Space>
      </Form>
    </CustomModal>
  );
}
